import { WmButton, WmToggletip } from "@watermarkinsights/ripple-react";
import translate from "@/i18n/translate";
import { AssessmentProgressByCourse } from "./ProgramCurriculumEngagement";
import AssessmentProgressChart from "../AssessmentProgressChart";
import { useIntl } from "react-intl";
import { Fragment } from "react";

interface CourseAssessmentProps {
  assessmentProgressByCourse: AssessmentProgressByCourse[];
  openFlyoutCourseDetails: (
    assessmentProgressByCourse: AssessmentProgressByCourse
  ) => void;
}

const RenderCourseAssessments = ({
  assessmentProgressByCourse,
  openFlyoutCourseDetails
}: CourseAssessmentProps) => {
  if (
    Array.isArray(assessmentProgressByCourse) &&
    !assessmentProgressByCourse.length
  ) {
    return <EmptyState />;
  } else {
    return (
      <div
        id="table-curriculum-engagement"
        className="curriculum-engagement-table"
      >
        <Table>
          <TableHeader
            columns={[
              translate(
                "app.programCurriculumEngagement.studentSubmissionRate"
              ),
              translate("app.programCurriculumEngagement.facultyAssessmentRate")
            ]}
          />
          <TableBody
            assessmentProgressByCourse={assessmentProgressByCourse}
            openFlyoutCourseDetails={openFlyoutCourseDetails}
          />
        </Table>
      </div>
    );
  }
};

const EmptyState = () => {
  return (
    <div className="academic-yr-empty-state flex-row">
      <div>
        <div className="empty-state-title">
          {translate("app.programCurriculumEngagement.EmptyState")}
        </div>
        <div className="empty-state-desc">
          {translate("app.programCurriculumEngagement.termEmptyStateDesc")}
        </div>
      </div>
    </div>
  );
};

const Table = (props: any) => {
  return <table className="data-table -shadow">{props.children}</table>;
};
const TableBody = ({
  assessmentProgressByCourse,
  openFlyoutCourseDetails
}: CourseAssessmentProps) => {
  const intl = useIntl();
  
  return (
    <tbody className="databody">
      {assessmentProgressByCourse.map(
        (courseAssessment: AssessmentProgressByCourse, index: number) => {
          const studentEnrolled =
            courseAssessment.assessmentProgress.studentEnrolled || 0;
          return (
            <Fragment key={`${index}-${courseAssessment.uuid}`}>
              <tr className="datarow">
                <td className="datacell" data-th={intl.formatMessage({ id: "app.missionAndOutcomes.course" })}>
                  <CourseCell
                    courseAssessment={courseAssessment}
                    openFlyoutCourseDetails={openFlyoutCourseDetails}
                  />
                </td>
                <td className="datacell submission-rate" data-th={intl.formatMessage({ id: "app.programCurriculumEngagement.studentSubmissionRate" })}>
                  <div className="progress-chart">
                    <RenderStudentSubmission
                      courseAssessment={courseAssessment}
                    />
                  </div>
                </td>

                <td className="datacell submission-rate" data-th={intl.formatMessage({ id: "app.programCurriculumEngagement.facultyAssessmentRate" })}>
                  <RenderFacultyAssessment
                    courseAssessment={courseAssessment}
                  />
                </td>
              </tr>
            </Fragment>
          );
        }
      )}
    </tbody>
  );
};
export const TableHeader = (props: any) => {
  const intl = useIntl();
  
  return (
    <thead id="program-engagement-table-header" className="dataheader">
      <tr className="dataheaderrow">
        <th
          className="sortable-table-header dataheadercell"
          data-th={props.columns[0]}
        >
          <div>
            <span>{translate("app.missionAndOutcomes.course")}</span>
            <div className="arrow-position">
              <span className="mdi mdi-arrow-down sort-arrow"></span>
            </div>
          </div>
        </th>
        {props.columns.map((column: string, index: number) => {
          return (
            <th
              className="dataheadercell"
              data-th={column}
              key={`column-${index}`}
            >
              {column}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

interface RenderStudentSubmissionProps {
  courseAssessment: AssessmentProgressByCourse;
}

const RenderStudentSubmission = ({
  courseAssessment
}: RenderStudentSubmissionProps) => {
  const tooltipCompletedTextStudent = useIntl().formatMessage({
    id: "app.programCurriculumEngagement.tooltipCompletedTextStudent"
  });
  const tooltipNotCompletedTextStudent = useIntl().formatMessage({
    id: "app.programCurriculumEngagement.tooltipNotCompletedTextStudent"
  });
  const { sectionCount, assessmentProgress } = courseAssessment;

  if (
    showSubmissionRateChart(
      sectionCount,
      assessmentProgress.studentEnrolled,
      assessmentProgress.submissionRate
    )
  ) {
    return (
      <AssessmentProgressChart
        amount={Number(assessmentProgress.submissionRate)}
        actualCount={Number(assessmentProgress.actualStudentSubmission)}
        expectedCount={Number(assessmentProgress.expectedStudentSubmission)}
        tooltipCompletedText={tooltipCompletedTextStudent}
        tooltipNotCompletedText={tooltipNotCompletedTextStudent}
      />
    );
  }

  if (
    checkNoSubmissionAndAssessmentRate(
      sectionCount,
      assessmentProgress.studentEnrolled,
      assessmentProgress.submissionRate,
      assessmentProgress.assessmentRate
    )
  ) {
    return (
      <div className="no-data no-assesments">
        {translate("app.programCurriculumEngagement.noAssesments")}
      </div>
    );
  }

  return <div className="no-data">{translate("app.na")}</div>;
};

interface RenderFacultyAssessmentProps {
  courseAssessment: AssessmentProgressByCourse;
}
interface CourseCellProps {
  courseAssessment: AssessmentProgressByCourse;
  openFlyoutCourseDetails: (
    assessmentProgressByCourse: AssessmentProgressByCourse
  ) => void;
}

function CourseCell({
  courseAssessment,
  openFlyoutCourseDetails
}: CourseCellProps) {
  const studentEnrolled =
    courseAssessment.assessmentProgress.studentEnrolled || 0;
  return (
    <div className="course">
      <WmButton
        button-type="textonly"
        className="courseName"
        onClick={() => openFlyoutCourseDetails(courseAssessment)}
        disabled={courseAssessment.sectionCount === 0}
      >
        <span>{`${courseAssessment.code} `}</span>
        <span>{courseAssessment.name}</span>
      </WmButton>
      <div>
        <span>{`${courseAssessment.sectionCount} `}</span>
        <span>{translate("app.programCurriculumEngagement.sections")}</span>
      </div>
      <div>
        <span>{`${studentEnrolled} `}</span>
        <span>
          {translate("app.programCurriculumEngagement.studentsEnrolled")}
        </span>
      </div>
    </div>
  );
}

const RenderFacultyAssessment = ({
  courseAssessment
}: RenderFacultyAssessmentProps) => {
  const tooltipCompletedTextFaculty = useIntl().formatMessage({
    id: "app.programCurriculumEngagement.tooltipCompletedTextFaculty"
  });
  const tooltipNotCompletedTextFaculty = useIntl().formatMessage({
    id: "app.programCurriculumEngagement.tooltipNotCompletedTextFaculty"
  });
  const toggeleTooltipText = useIntl().formatMessage({
    id: "app.programCurriculumEngagement.toggleTooltipText"
  });

  const { sectionCount, assessmentProgress } = courseAssessment;

  if (
    showAssesmentRate(
      sectionCount,
      assessmentProgress.studentEnrolled,
      assessmentProgress.assessmentRate,
      assessmentProgress.submissionRate
    )
  ) {
    return (
      <AssessmentProgressChart
        amount={Number(assessmentProgress.assessmentRate)}
        actualCount={Number(assessmentProgress.actualCompletedAssessments)}
        expectedCount={Number(assessmentProgress.expectedCompletedAssessments)}
        tooltipCompletedText={tooltipCompletedTextFaculty}
        tooltipNotCompletedText={tooltipNotCompletedTextFaculty}
      />
    );
  }

  if (
    checkNoSubmissionRate(
      sectionCount,
      assessmentProgress.studentEnrolled,
      assessmentProgress.submissionRate
    )
  ) {
    return <></>;
  }

  if (
    checkSubmissionRateZero(
      courseAssessment.sectionCount,
      courseAssessment.assessmentProgress.studentEnrolled,
      courseAssessment.assessmentProgress.submissionRate
    )
  ) {
    return (
      <div className="no-data">
        <span className="no-data-toggletip">{translate("app.na")}</span>
        <WmToggletip
          tooltip={toggeleTooltipText}
          tooltip-position="top"
          class="toggletip"
        ></WmToggletip>
      </div>
    );
  }

  return <div className="no-data">{translate("app.na")}</div>;
};

function showSubmissionRateChart(
  sectionCount: number | null,
  studentsEnrolled: number | null,
  submissionRate: number | null
) {
  return sectionCount && studentsEnrolled && submissionRate !== null;
}
function checkNoSubmissionAndAssessmentRate(
  sectionCount: number | null,
  studentsEnrolled: number | null,
  submissionRate: number | null,
  assesmentRate: number | null
) {
  return (
    sectionCount &&
    studentsEnrolled &&
    submissionRate === null &&
    assesmentRate === null
  );
}
function checkNoSubmissionRate(
  sectionCount: number | null,
  studentsEnrolled: number | null,
  submissionRate: number | null
) {
  return sectionCount && studentsEnrolled && submissionRate === null;
}
function showAssesmentRate(
  sectionCount: number | null,
  studentsEnrolled: number | null,
  assesmentRate: number | null,
  submissionRate: number | null
) {
  return (
    sectionCount &&
    studentsEnrolled &&
    assesmentRate !== null &&
    submissionRate !== 0
  );
}

function checkSubmissionRateZero(
  sectionCount: number | null,
  studentsEnrolled: number | null,
  submissionRate: number | null
) {
  return sectionCount && studentsEnrolled && submissionRate === 0;
}

export default RenderCourseAssessments;
